import React, { FC, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { When } from 'react-if'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ContinueWatchingCollection } from '@/organisms/WatchNowCollection/ContinueWatchingCollection'
import {
  GET_RESUME_WATCHING,
  isResumeWatchingEdge,
} from '@/organisms/WatchNowCollection/ContinueWatchingCollection/data'
import { StartWatchingCollection } from '@/organisms/WatchNowCollection/StartWatchingCollection'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel } from '@/services/RenderService'
import { useUser } from '@/services/UserService'
import { ResumeWatchingQuery } from '@/types/codegen-federation'
import { isDefined } from '@/utils/types'
import { PlaceholderLandscapeCollection } from '../PlaceholderLandscapeCollection'
import { TopPicksForYouCollection } from './TopPicksForYouCollection'

export interface WatchNowCollectionProps {
  contentLinks: LinkViewModel[]
  title: string
  onlyShowContinue?: boolean
  onlyShowFree?: boolean
}

export const WatchNowCollection: FC<WatchNowCollectionProps> = ({
  contentLinks,
  title,
  onlyShowContinue = false,
  onlyShowFree = false,
}) => {
  const { isLoggedIn = false, loading: isLoadingUser = false } = useUser()
  const { isGuildMember } = useGuildMember()

  const { data, loading: isLoadingContinueWatching } = useQuery<ResumeWatchingQuery>(GET_RESUME_WATCHING, {
    errorPolicy: 'all',
    context: {
      requireAuth: true,
    },
    variables: {
      first: 50,
      after: null,
    },
  })

  const projectsToContinue = useMemo(
    () =>
      data?.resumeWatching?.edges
        ?.filter((edge) => edge?.node?.position)
        ?.filter(isDefined)
        ?.filter((edge) => edge?.node?.content && 'project' in edge.node.content && edge.node.content.project?.slug)
        ?.filter(isResumeWatchingEdge) ?? [],
    [data?.resumeWatching?.edges],
  )

  if (isLoadingUser || isLoadingContinueWatching) {
    return <PlaceholderLandscapeCollection />
  }

  const freeContentLinks = contentLinks.filter((contentLink) => {
    const episode = contentLink?.metadata?.episode as
      | { earlyAccessDate?: string; publiclyAvailableDate?: string }
      | undefined

    if (!episode?.publiclyAvailableDate) {
      return false
    }

    const publiclyAvailableDate = new Date(episode.publiclyAvailableDate)
    const now = new Date()

    return publiclyAvailableDate < now
  })

  if (isLoggedIn && projectsToContinue.length > 0) {
    return (
      <>
        <PaddedContainer className="overflow-x-hidden" fluid>
          <div className="flex flex-col gap-y-12">
            <When condition={onlyShowContinue}>
              <ContinueWatchingCollection toContinueWatching={projectsToContinue} />
            </When>
            <When condition={!onlyShowContinue}>
              <ContinueWatchingCollection toContinueWatching={projectsToContinue} />
              {isGuildMember ? (
                <StartWatchingCollection
                  contentLinks={onlyShowFree ? freeContentLinks : contentLinks}
                  toContinueWatching={projectsToContinue}
                  title={title}
                />
              ) : (
                <TopPicksForYouCollection />
              )}
            </When>
          </div>
        </PaddedContainer>
      </>
    )
  } else {
    return (
      <>
        <When condition={!onlyShowContinue}>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <StartWatchingCollection contentLinks={onlyShowFree ? freeContentLinks : contentLinks} title={title} />
          </PaddedContainer>
        </When>
      </>
    )
  }
}
